import { memo, useEffect, useState, Fragment } from 'react';
import { useHistory } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getJobCategory } from '../_api';
import DetailEntry from '../layouts/DetailEntry';
import DetailLayout from '../layouts/DetailLayout';
import TrainingTrackDetails from '../TrainingTrack/DetailsView';
import ReactMarkdown from 'react-markdown';
import InputLabel from '@mui/material/InputLabel';
import FormControlCustomized from '../layouts/FormControlCustomized';
import { TRAINING_TRACK_MAX } from '../_const';
import { getSystemName } from '../_utils';

/**
 * Epic Job Category search results view
 * @param {any} id - primary key to identify the job category
 * @returns job category search result details
 */
const DetailsView = memo(function JobCategoryDetails({
	id,
	enableLinks = false,
}) {
	const [sShowJobCategories, setShowJobCategories] = useState(false);
	const [sJobCategory, setJobCategory] = useState(null);
	const [sErrorMessage, setErrorMessage] = useState(null);
	const history = useHistory();

	/**
	 * trigger API call to get job category details upon page render
	 */
	useEffect(() => {
		let active = true;

		async function fetchApi() {
			try {
				const selectedJobCategory = await getJobCategory(id);
				setJobCategory(selectedJobCategory);
			} catch {
				setErrorMessage(
					'Encountered error when trying to fetch information on Job Category ID = "' +
						id +
						'"'
				);
			}
		}

		if (active) {
			fetchApi();
		}

		return () => {
			active = false;
		};
	}, [id]);

	/**
	 * Toggle job category section visibility
	 * @param {boolean} expand
	 */
	const onExpandJobCategoriesHandler = async expand => {
		setShowJobCategories(expand);
	};

	return (
		<>
			{sErrorMessage && (
				<Alert
					severity='error'
					variant='filled'
					onClose={() => {
						setErrorMessage(null);
					}}
				>
					{sErrorMessage}
				</Alert>
			)}
			{sJobCategory?.jobCategoryID && (
				<DetailLayout>
					<DetailEntry
						label={'ID'}
						value={sJobCategory?.jobCategoryID}
					/>
					<DetailEntry
						label={'Name'}
						value={
							enableLinks && sJobCategory?.name ? (
								<Link
									underline='always'
									onClick={() =>
										history.push(
											'/EpicUtilities/TrainingTrack/Search?id=' +
												sJobCategory.jobCategoryID
										)
									}
									sx={{
										cursor: 'pointer',
									}}
								>
									{sJobCategory?.name}
								</Link>
							) : (
								sJobCategory?.name
							)
						}
					/>
					<DetailEntry
						label={'State'}
						value={sJobCategory?.active ? 'active' : 'inactive'}
					/>
					<DetailEntry
						label={'Description'}
						value={
							!!sJobCategory?.description
								? sJobCategory?.description
								: 'N/A'
						}
					/>
					<DetailEntry
						label={'Type'}
						value={
							!!sJobCategory?.type ? sJobCategory?.type : 'N/A'
						}
					/>
					<DetailEntry
						label={'System Name'}
						value={getSystemName(sJobCategory?.appID)}
					/>
					<DetailEntry
						label={'Owning Application(s)'}
						value={
							!!sJobCategory?.owningApplication
								? sJobCategory?.owningApplication
								: 'N/A'
						}
					/>
					<DetailEntry
						label={'Template ID'}
						value={
							!!sJobCategory?.templateID
								? sJobCategory?.templateID
								: 'N/A'
						}
					/>
					<DetailEntry
						label={'Template Name'}
						value={
							!!sJobCategory?.templateName
								? sJobCategory?.templateName
								: 'N/A'
						}
					/>
					<DetailEntry
						label={'Subtemplate ID(s)'}
						value={
							!!sJobCategory?.subtemplateIDs
								? sJobCategory?.subtemplateIDs
								: 'N/A'
						}
					/>
					<DetailEntry
						label={'Subtemplate Name(s)'}
						value={
							!!sJobCategory?.subtemplateNames
								? sJobCategory?.subtemplateNames
								: 'N/A'
						}
					/>
					<DetailEntry
						label={'Is Provider?'}
						value={
							sJobCategory?.isProvider === true
								? 'Yes'
								: sJobCategory?.isProvider === false
								? 'No'
								: 'N/A'
						}
					/>
					<DetailEntry
						label={'Requires SER?'}
						value={sJobCategory?.SER ? 'Yes' : 'No'}
					/>
					<DetailEntry
						label={
							'What is the SER Blueprint ID that should be applied to this Job Category?'
						}
						value={
							!!sJobCategory?.SERBlueprintID
								? sJobCategory?.SERBlueprintID
								: 'N/A'
						}
					/>
					<DetailEntry
						label={'Day in the life activity (pre-go live)'}
						value={
							!!sJobCategory?.dayInTheLifeActivityPreLive
								? sJobCategory?.dayInTheLifeActivityPreLive
								: 'N/A'
						}
					/>
					<DetailEntry
						label={'Day in the life activity (post-go live)'}
						value={
							!!sJobCategory?.dayInTheLifeActivityPostLive
								? sJobCategory?.dayInTheLifeActivityPostLive
								: 'N/A'
						}
					/>
					<DetailEntry
						label={'Note'}
						value={
							!!sJobCategory?.note ? (
								<>
									{sJobCategory?.note}
									<FormControlCustomized>
										<Box
											sx={{
												border: 'solid',
												borderWidth: '1px',
												borderRadius: '4px',
												borderColor: 'lightgray',
												p: 2,
												width: '100%',
												ml: 1,
												mr: 1,
											}}
										>
											<InputLabel
												sx={{
													backgroundColor: 'white',
													pl: 1,
													pr: 1,
													ml: 1,
													mr: 1,
												}}
												shrink
											>
												Note Preview
											</InputLabel>
											<ReactMarkdown
												components={{
													a: props => (
														<a
															href={props.href}
															target='_blank'
															rel='noreferrer'
														>
															{props.children}
														</a>
													),
												}}
											>
												{sJobCategory?.note}
											</ReactMarkdown>
										</Box>
									</FormControlCustomized>
								</>
							) : (
								'N/A'
							)
						}
					/>
					<>
						<Box
							display='flex'
							justifyContent='space-between'
							alignItems='center'
						>
							<DetailEntry
								label={'Associated Training Tracks'}
								disableLoading
							/>

							<IconButton
								onClick={() =>
									onExpandJobCategoriesHandler(
										!sShowJobCategories
									)
								}
							>
								<ExpandMoreIcon />
							</IconButton>
						</Box>
						{sShowJobCategories && (
							<>
								{Array(TRAINING_TRACK_MAX)
									.fill(0)
									.map((_, index) => {
										return sJobCategory[
											'trainingTrack' + (index + 1)
										] ? (
											<Fragment
												key={'training-track-' + index}
											>
												<Divider>
													{'Training Track ' +
														(index + 1)}
												</Divider>
												<TrainingTrackDetails
													id={
														sJobCategory[
															'trainingTrack' +
																(index + 1)
														]
													}
													hideJobCategories
													enableLinks
												/>
											</Fragment>
										) : (
											<Fragment
												key={'training-track-' + index}
											>
												<Divider>
													{'Training Track ' +
														(index + 1)}
												</Divider>
												<DetailEntry
													label={
														'Training Track ' +
														(index + 1)
													}
													value={'N/A'}
												/>
											</Fragment>
										);
									})}
							</>
						)}
					</>
				</DetailLayout>
			)}
		</>
	);
});

export default DetailsView;
